// src/pages/ProductDetailPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import {
    ShoppingCart, Check, AlertCircle,
    ArrowLeft
} from 'lucide-react';
import './ProductDetailPage.css';
import { Product, ProductPlan } from '../types/marketplace.types';
import { ContentBlock } from '../types/contentBlocks.types';

// ✅ IMPORTAR BLOCOS
import { TextBlock } from '../components/productDetail/TextBlock';
import { StatsBlock } from '../components/productDetail/StatsBlock';
import { VideoBlock } from '../components/productDetail/VideoBlock';
import { FlashcardPreviewBlock } from '../components/productDetail/FlashcardPreviewBlock';
import { ContentStructureBlock } from '../components/productDetail/ContentStructureBlock'; // ✅ ADICIONAR

const API_BASE_URL = '/api';

const ProductDetailPage: React.FC = () => {
    const { productId } = useParams<{ productId: string }>();
    const navigate = useNavigate();
    const { addItem, hasItem, removeItem } = useCart();

    const [product, setProduct] = useState<Product | null>(null);
    const [selectedPlan, setSelectedPlan] = useState<ProductPlan | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [alreadyPurchased, setAlreadyPurchased] = useState(false);
    const [couponFromUrl, setCouponFromUrl] = useState<string | null>(null);
    const [couponInfo, setCouponInfo] = useState<any>(null);

    // ✅ NOVO: Estado dos blocos
    const [contentBlocks, setContentBlocks] = useState<ContentBlock[]>([]);

    const loadProduct = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');

            const response = await fetch(`${API_BASE_URL}/marketplace/products/${productId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!response.ok) {
                throw new Error('Produto não encontrado');
            }

            const data = await response.json();
            setProduct(data.product);
            setAlreadyPurchased(data.product.alreadyPurchased);

            // ✅ CARREGAR BLOCOS
            if (data.product.contentBlocks && data.product.contentBlocks.length > 0) {
                setContentBlocks(data.product.contentBlocks);
            }

            // Selecionar primeiro plano por padrão
            if (data.product.plans && data.product.plans.length > 0) {
                setSelectedPlan(data.product.plans[0]);
            }

        } catch (err) {
            console.error('Erro ao carregar produto:', err);
            setError('Produto não encontrado ou indisponível');
        } finally {
            setLoading(false);
        }
    }, [productId]);

    const validateCoupon = useCallback(async (couponCode: string) => {
        if (!product || !selectedPlan) return;

        try {
            const token = localStorage.getItem('accessToken');

            const response = await fetch(`${API_BASE_URL}/payments/validate-coupon`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    couponCode,
                    productId: product._id,
                    planIndex: product.plans.findIndex(p => p._id === selectedPlan._id)
                })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.valid) {
                    setCouponInfo(data);
                }
            }
        } catch (error) {
            console.error('Erro ao validar cupom:', error);
        }
    }, [product, selectedPlan]);

    useEffect(() => {
        if (productId) {
            loadProduct();

            // Capturar cupom da URL
            const params = new URLSearchParams(window.location.search);
            const couponCode = params.get('coupon');

            if (couponCode) {
                setCouponFromUrl(couponCode);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productId]);  // ← SÓ productId!

    useEffect(() => {
        if (couponFromUrl && product && selectedPlan) {
            validateCoupon(couponFromUrl);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [couponFromUrl, product, selectedPlan]);

    const formatPrice = (cents: number) => {
        return (cents / 100).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        });
    };

    const getPlanLabel = (plan: ProductPlan) => {
        if (plan.durationMonths === null) return 'Acesso Vitalício';
        if (plan.durationMonths === 6) return '6 Meses';
        if (plan.durationMonths === 12) return '12 Meses';
        return `${plan.durationMonths} meses`;
    };

    const handleAddToCart = () => {
        if (!selectedPlan || !product) return;
        addItem(product, selectedPlan, couponFromUrl || undefined);
        // permanece na página — o CartBanner já aparece automaticamente
    };

    // ✅ RENDERIZADOR DE BLOCOS
    const renderBlock = (block: ContentBlock) => {
        switch (block.type) {
            case 'text':
                return (
                    <TextBlock
                        key={block._id || block.order}
                        title={block.data.title}
                        content={block.data.content}
                        alignment={block.data.alignment}
                    />
                );

            case 'stats':
                return (
                    <StatsBlock
                        key={block._id || block.order}
                        items={block.data.items}
                    />
                );

            case 'video':
                return (
                    <VideoBlock
                        key={block._id || block.order}
                        url={block.data.url}
                        title={block.data.title}
                        description={block.data.description}
                    />
                );

            case 'flashcardPreview':
                return (
                    <FlashcardPreviewBlock
                        key={block._id || block.order}
                        deckId={block.data.deckId}
                        subdeckId={block.data.subdeckId}
                        cardIds={block.data.cardIds}
                    />
                );

            case 'contentStructure':
                return <ContentStructureBlock deckIds={block.data.deckIds} />;


            default:
                return null;
        }
    };

    if (loading) {
        return (
            <div className="prdth-page">
                <div className="prdth-loading">
                    <div className="prdth-spinner"></div>
                    <p>Carregando produto...</p>
                </div>
            </div>
        );
    }

    if (error || !product) {
        return (
            <div className="prdth-page">
                <div className="prdth-error-container">
                    <AlertCircle size={64} strokeWidth={1.5} />
                    <h2>{error || 'Produto não encontrado'}</h2>
                    <button onClick={() => navigate('/marketplace')}>
                        Voltar ao Marketplace
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="prdth-page">
            {/* Hero Header */}
            <div className="prdth-hero-header">
                <div className="prdth-hero-grid" aria-hidden />
                <div className="prdth-hero-inner">

                    {/* Esquerda: botão voltar */}
                    <button className="prdth-btn-back" onClick={() => navigate('/marketplace')}>
                        <ArrowLeft size={18} />
                        Voltar ao Catálogo
                    </button>

                    {/* Centro: tipo + nome */}
                    <div className="prdth-hero-meta">
                        <span className="prdth-hero-type">
                            {product?.type === 'deck' ? 'Deck Individual' :
                                product?.type === 'combo' ? 'Combo de Decks' : 'Acesso Total'}
                        </span>
                        <h1 className="prdth-hero-title">{product?.title}</h1>
                        {product?.description && (
                            <p className="prdth-hero-desc">{product.description}</p>
                        )}
                    </div>

                    {/* Direita: badge carrinho */}
                    <div className="prdth-hero-right">
                        {hasItem(product._id) && (
                            <div className="prdth-hero-cart-badge">
                                <Check size={14} strokeWidth={3} />
                                No Carrinho
                            </div>
                        )}
                    </div>

                </div>
            </div>

            {/* Content */}
            <div className="prdth-container">
                {/* Left: Product Info */}
                <div className="prdth-info-section">

                    {/* Desconto do Cupom */}
                    {couponInfo && couponInfo.coupon.discountPercent > 0 && (
                        <div className="prdth-coupon-discount-badge">
                            <span className="prdth-discount-icon">🎟️</span>
                            <div className="prdth-discount-info">
                                <strong>{couponInfo.coupon.discountPercent}% de desconto</strong>
                                <span>Cupom aplicado automaticamente</span>
                            </div>
                        </div>
                    )}

                    {/* Descrição Curta (sempre aparece) */}
                    {product.description && (
                        <p className="prdth-description-short">{product.description}</p>
                    )}

                    {/* ✅ RENDERIZAR BLOCOS DE CONTEÚDO */}
                    {contentBlocks.length > 0 && (
                        <div className="prdth-content-blocks">
                            {contentBlocks
                                .sort((a, b) => a.order - b.order)
                                .map((block, index) => ( // ✅ ADICIONAR index
                                    <React.Fragment key={block._id || `block-${index}`}> {/* ✅ ADICIONAR key */}
                                        {renderBlock(block)}
                                    </React.Fragment>
                                ))}
                        </div>
                    )}
                </div>

                {/* Right: Purchase Box */}
                <div className="prdth-purchase-box">
                    {alreadyPurchased ? (
                        <div className="prdth-already-purchased">
                            <Check size={48} strokeWidth={2} />
                            <h3>Você já possui este produto</h3>
                            <p>Acesse no seu dashboard para começar a estudar</p>
                            <button
                                className="prdth-btn-primary"
                                onClick={() => navigate('/dashboard')}
                            >
                                Ir para Dashboard
                            </button>
                        </div>
                    ) : (
                        <>
                            <h3>Escolha seu plano</h3>

                            <div className="prdth-plans-list">
                                {product.plans.map((plan) => (
                                    <div
                                        key={plan._id}
                                        className={`prdth-plan-card ${selectedPlan?._id === plan._id ? 'prdth-selected' : ''}`}
                                        onClick={() => setSelectedPlan(plan)}
                                    >
                                        <div className="prdth-plan-header">
                                            <div className="prdth-plan-info">
                                                <span className="prdth-plan-label">
                                                    {getPlanLabel(plan)}
                                                </span>
                                                <span className="prdth-plan-price">
                                                    {formatPrice(plan.priceCents)}
                                                </span>
                                            </div>
                                            <div className="prdth-plan-radio">
                                                {selectedPlan?._id === plan._id && (
                                                    <Check size={16} strokeWidth={3} />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {selectedPlan && (
                                <div className="prdth-purchase-summary">
                                    <div className="prdth-summary-row">
                                        <span>Subtotal</span>
                                        <span>{formatPrice(selectedPlan.priceCents)}</span>
                                    </div>

                                    {couponInfo && couponInfo.pricing.discountCents > 0 && (
                                        <div className="prdth-summary-row prdth-discount">
                                            <span>Desconto ({couponInfo.coupon.discountPercent}%)</span>
                                            <span>- {formatPrice(couponInfo.pricing.discountCents)}</span>
                                        </div>
                                    )}

                                    <div className="prdth-summary-row prdth-total">
                                        <span>Total</span>
                                        <span>
                                            {couponInfo
                                                ? formatPrice(couponInfo.pricing.finalPriceCents)
                                                : formatPrice(selectedPlan.priceCents)
                                            }
                                        </span>
                                    </div>
                                </div>
                            )}

                            {hasItem(product._id) ? (
                                <div className="prdth-cart-actions">
                                    <button
                                        className="prdth-btn-in-cart"
                                        onClick={() => navigate('/checkout')}
                                    >
                                        <Check size={20} strokeWidth={2.5} />
                                        No Carrinho — Ver Carrinho
                                    </button>
                                    <button
                                        className="prdth-btn-remove-cart"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removeItem(product._id);
                                        }}
                                    >
                                        Remover do Carrinho
                                    </button>
                                </div>
                            ) : (
                                <button
                                    className="prdth-btn-buy-now"
                                    onClick={handleAddToCart}
                                    disabled={!selectedPlan}
                                >
                                    <ShoppingCart size={20} />
                                    Adicionar ao Carrinho
                                </button>
                            )}

                            <div className="prdth-payment-info">
                                <p>
                                    <Check size={16} />
                                    Pagamento seguro via Mercado Pago
                                </p>
                                <p>
                                    <Check size={16} />
                                    Acesso imediato após confirmação
                                </p>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProductDetailPage;